<template>
  <div class="d-book-user-reviews-list">
    <div class="user-reviews-header grid-flex grid-middle">
      <div class="grid-item">
        <h2>
          Reader reviews
        </h2>
      </div>
      <div class="grid-item-shrink">
        <DuiDropdown
          :disabled="(!isInitialized || !entries.length)"
          label="Sort by"
          :label-width="64"
          store-load-key="BookUserReviews/get-sorting-options"
          store-update-key="BookUserReviews/selectSortingOption"
          value="created_at"
        />
      </div>
    </div>

    <div v-if="isInitialized">
      <div
        v-if="!entries.length"
        class="no-entries text-center padding-top-4em padding-bottom-4em"
      >
        <img
          alt="No books yet"
          :src="emptyImageSrc"
        >
        <h2>
          No user reviews yet
        </h2>

        <div v-if="isReviewable">
          <p class="large">
            Have you read this book? Write a review and share your
            thoughts with other readers.
          </p>

          <DBookWriteUserReviewButton
            :bookshelf="bookshelf"
            :book-url="bookUrl"
            :book-slug="bookSlug"
            class="button-lg space-top-2em"
            :is-user-signed-in="isUserSignedIn"
          />
        </div>
      </div>

      <div
        v-else
        class="user-reviews-list"
      >
        <DBookUserReviewEntry
          v-for="userReview in entries"
          :key="userReview.slug"
          :is-user-signed-in="isUserSignedIn"
          :user-review="userReview"
        />
      </div>
    </div>

    <div ref="loadMoreUserReviews" />

    <DuiLoadingIndicator
      v-if="isLoading"
      size="lg"
    />
  </div>
</template>

<script>
import USER_REVIEWS_IMG from '@d-assets/user-reviews.svg';
import DBookWriteUserReviewButton from '@d-components/d-book-write-user-review-panel/d-book-write-user-review-button/d-book-write-user-review-button.vue';
import { whenElementIsVisible } from '@dui/helpers/util-funcs';

import DBookUserReviewEntry from './d-book-user-review-entry/d-book-user-review-entry.vue';

export default {
  components: {
    DBookUserReviewEntry,
    DBookWriteUserReviewButton,
  },
  props: {
    bookshelf: {
      type: String,
      default: null,
    },
    bookSlug: {
      type: String,
      required: true,
    },
    bookUrl: {
      type: String,
      required: true,
    },
    isReviewable: {
      type: Boolean,
      default: false,
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    disableLoadMore: null,
    emptyImageSrc: USER_REVIEWS_IMG,
  }),
  computed: {
    entries() {
      return this.$store.getters['BookUserReviews/get-entries'];
    },
    hasMoreResults() {
      return this.$store.getters['BookUserReviews/get-has-more-results'];
    },
    isInitialized() {
      return this.$store.getters['BookUserReviews/get-initialized-state'];
    },
    isLoading() {
      return this.$store.getters['BookUserReviews/get-loading-state'];
    },
  },
  watch: {
    isLoading: function() {
      this.configureLoadMore();
    },
  },
  beforeDestroy() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  },
  mounted() {
    this.setUserReviewsListPresence();
    this.loadUserReviews();
  },
  methods: {
    configureLoadMore() {
      if (!this.isLoading && this.hasMoreResults) {
        this.enableLoadMore();
        return;
      }

      if (this.disableLoadMore) {
        this.disableLoadMore();
      }
    },

    enableLoadMore() {
      if (this.disableLoadMore) {
        this.disableLoadMore();
      }

      if (!this.$refs.loadMoreUserReviews) {
        return;
      }

      const disableLoadMore = whenElementIsVisible(
        this.$refs.loadMoreUserReviews,
        this.loadUserReviews.bind(this),
      );

      this.$set(this, 'disableLoadMore', disableLoadMore);
    },

    loadUserReviews() {
      this.$store.dispatch('BookUserReviews/getEntries', this.bookSlug);
    },

    setUserReviewsListPresence() {
      this.$store.dispatch('BookUserReviews/setUserReviewsListPresence');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-book-user-reviews-list {
  > .dui-loading-indicator {
    padding: $_4em 0;
    text-align: center;
    width: 100%;
  }

  .user-reviews-header {
    border-bottom: $border-width-base solid getColor($grey, medium);
    padding-bottom: $_2em;

    h2 {
      margin-bottom: $_2em;

      @include screenMoreThan(xs) {
        margin: 0;
      }
    }
  }
}
</style>
